/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React, { useEffect } from 'react'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { useAppDispatch } from '../../../../app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from '../../../../hooks/useDashboardState'
import { getWidgetForDashboard } from '../../../../features/widget/asyncThunks'
import { createInsightCount } from '../../../../utils/runSearchUtil'
import { getInsightCategory } from '../../../../utils/viewOptionUtil'
import { toFilter } from '../../../../utils/widgetUtil'
import Measure from '../../../../enums/Measure'
import { SocialWebSentimentWidget } from '../../../../schemas/SocialWebSentimentWidgetSchema'
import { Dashboard } from '../../../../schemas/DashboardSchema'
import { RunSearch } from '../../../../schemas/action/RunSearchSchema'

const createSearch = (
  widgetSetting: SocialWebSentimentWidget,
  dashboardData: Dashboard
): RunSearch => {
  const filter = toFilter(widgetSetting)
  // FIXME: Not sure the impact, but some extra properties `widgetSetting.excludedTerms` that weren't exsit originally
  // originally this field is lost during `nb.dto.clone(dashboard)` process in `onSuccess_getDashboard_` in show.js

  const measure = Measure.MENTIONS

  const { precision, selectedTopic, viewOption } = widgetSetting
  const { id: dashboardId, name } = dashboardData

  return createInsightCount(
    dashboardId!,
    name!,
    precision!,
    [selectedTopic.topicId!],
    filter,
    measure,
    getInsightCategory(viewOption!)
  )
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
}

const RunSearchEffect = ({ widgetId, children }: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebSentimentWidget>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const search = createSearch(widgetSetting, dashboardData!)

    dispatch(
      getWidgetForDashboard({
        searches: [search],
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
